import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import { placementFormLoader, placementViewLoader } from "../loaders/placement";
import { PlacementBreadcrumb } from "../../breadcrumbs/placement";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";

const { PLACEMENT } = ENTITY;

export const placementRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const placementRoute = createRoute({
    getParentRoute: () => parent,
    path: PLACEMENT.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: PLACEMENT.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: PLACEMENT.label.upFirstPlural,
    },
  });

  // List placements

  const placementListRoute = createRoute({
    getParentRoute: () => placementRoute,
    path: "/",
    pendingComponent: () => <EntityListLoading entity={PLACEMENT} />,
  }).lazy(() => import("./placement.lazy").then((d) => d.PlacementListRoute));

  // Create placement

  const placementCreateRoute = createRoute({
    getParentRoute: () => placementRoute,
    path: "create",
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: PLACEMENT.type.singular, action: "CREATE" },
      ]),
    staticData: {
      breadcrumb: "Create new",
    },
    loader: placementFormLoader,
    pendingComponent: () => <EntityEditLoading entity={PLACEMENT} />,
  }).lazy(() => import("./placement.lazy").then((d) => d.PlacementCreateRoute));

  // View placement

  const placementViewRootRoute = createRoute({
    getParentRoute: () => placementRoute,
    path: "$placementId",
    staticData: {
      breadcrumb: PlacementBreadcrumb,
    },
    loader: placementViewLoader,
  });

  // Edit placement

  const placementEditRoute = createRoute({
    getParentRoute: () => placementViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: placementFormLoader,
    pendingComponent: () => <EntityEditLoading entity={PLACEMENT} />,
  }).lazy(() => import("./placement.lazy").then((d) => d.PlacementEditRoute));

  // View placement Layout

  const placementViewLayoutRoute = createRoute({
    getParentRoute: () => placementViewRootRoute,
    id: "placement-view-layout",
    pendingComponent: () => <EntityViewLoading entity={PLACEMENT} />,
  }).lazy(() => import("./placement.lazy").then((d) => d.PlacementViewLayoutRoute));

  // View placement - General Tab

  const placementViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => placementViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./placement.lazy").then((d) => d.PlacementViewLayoutGeneralTabRoute)
  );

  // View placement - About Tab

  const placementViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => placementViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./placement.lazy").then((d) => d.PlacementViewLayoutAboutTabRoute)
  );

  // Route tree
  placementRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [placementListRoute]),
    placementCreateRoute,
    placementViewRootRoute.addChildren([
      placementEditRoute,
      placementViewLayoutRoute.addChildren([
        placementViewLayoutGeneralTabRoute,
        placementViewLayoutAboutTabRoute,
      ]),
    ]),
  ]);

  return placementRoute;
};

const placementRoute = placementRouteFactory();

export default placementRoute;
